import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Edit2, Trash2, Waves, Coins, Home, Sparkles, CheckCircle2, Clock } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Skeleton } from '../../../components/ui/Skeleton'
import { RowActionsMenu } from '../../../components/ui/RowActionsMenu'
import { EmptyState } from '../../../components/EmptyState'
import { useConfirm } from '../../../contexts/ConfirmDialogContext'
import { cn } from '../../../lib/utils'

const KIND_META = {
  swimming: { icon: Waves, tone: 'text-sky-500' },
  jam3eya:  { icon: Coins, tone: 'text-amber-500' },
  rent:     { icon: Home,  tone: 'text-violet-500' },
  custom:   { icon: Sparkles, tone: 'text-muted-foreground' },
}

function fmt(n) {
  if (n == null || isNaN(n)) return '—'
  return Math.round(Number(n)).toLocaleString('en-US')
}

function ObligationForm({ initial, onSave, onCancel, submitting }) {
  const { t } = useTranslation()
  const [kind, setKind] = useState(initial?.kind || 'custom')
  const [name, setName] = useState(initial?.name || '')
  const [amount, setAmount] = useState(initial?.amount?.toString() || '')
  const [frequency, setFrequency] = useState(initial?.frequency || 'monthly')
  const [totalMonths, setTotalMonths] = useState(initial?.total_months?.toString() || '')
  const [remainingMonths, setRemainingMonths] = useState(initial?.remaining_months?.toString() || '')
  const [startDate, setStartDate] = useState(initial?.start_date || '')
  const [endDate, setEndDate] = useState(initial?.end_date || '')
  const [nextPaymentDate, setNextPaymentDate] = useState(initial?.next_payment_date || '')
  const [attributionMonth, setAttributionMonth] = useState(initial?.attribution_month || '')
  const [payoutStatus, setPayoutStatus] = useState(initial?.payout_status || '')
  const [payoutDate, setPayoutDate] = useState(initial?.payout_date || '')
  const [goalLink, setGoalLink] = useState(initial?.goal_link || '')
  const [notes, setNotes] = useState(initial?.notes || '')

  const submit = (e) => {
    e.preventDefault()
    onSave({
      kind,
      name: name.trim(),
      amount: Number(amount),
      frequency,
      total_months: totalMonths ? Number(totalMonths) : null,
      remaining_months: remainingMonths ? Number(remainingMonths) : null,
      start_date: startDate || null,
      end_date: endDate || null,
      next_payment_date: nextPaymentDate || null,
      attribution_month: attributionMonth.trim() || null,
      payout_status: payoutStatus || null,
      payout_date: payoutDate || null,
      goal_link: goalLink.trim() || null,
      notes: notes.trim() || null,
      status: 'active',
    })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      <div className="grid grid-cols-2 gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value)} dir="auto"
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm">
          <option value="swimming">{t('finance.obligations.kinds.swimming')}</option>
          <option value="jam3eya">{t('finance.obligations.kinds.jam3eya')}</option>
          <option value="rent">{t('finance.obligations.kinds.rent')}</option>
          <option value="custom">{t('finance.obligations.kinds.custom')}</option>
        </select>
        <select value={frequency} onChange={(e) => setFrequency(e.target.value)} dir="auto"
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm">
          <option value="monthly">{t('finance.obligations.frequencies.monthly')}</option>
          <option value="every_6_months">{t('finance.obligations.frequencies.every_6_months')}</option>
          <option value="one_time">{t('finance.obligations.frequencies.one_time')}</option>
        </select>
      </div>
      <input type="text" required maxLength={80}
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder={t('finance.obligations.name')}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" />
      <input type="number" step="0.01" required dir="ltr"
        value={amount} onChange={(e) => setAmount(e.target.value)}
        placeholder={t('finance.obligations.amount')}
        className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />

      {frequency === 'monthly' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" min={1} dir="ltr"
              value={totalMonths} onChange={(e) => setTotalMonths(e.target.value)}
              placeholder={t('finance.obligations.totalMonths')}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
            <input type="number" min={0} dir="ltr"
              value={remainingMonths} onChange={(e) => setRemainingMonths(e.target.value)}
              placeholder={t('finance.obligations.remainingMonths')}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} dir="ltr"
              placeholder={t('finance.obligations.startDate')}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} dir="ltr"
              placeholder={t('finance.obligations.endDate')}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
          </div>
        </>
      )}

      {frequency === 'every_6_months' && (
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={nextPaymentDate} onChange={(e) => setNextPaymentDate(e.target.value)} dir="ltr"
            placeholder={t('finance.obligations.nextPaymentDate')}
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
          <input type="text" value={attributionMonth} onChange={(e) => setAttributionMonth(e.target.value)} dir="ltr"
            placeholder="YYYY-MM"
            pattern="\d{4}-\d{2}"
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
        </div>
      )}

      {kind === 'jam3eya' && (
        <div className="grid grid-cols-2 gap-2">
          <select value={payoutStatus} onChange={(e) => setPayoutStatus(e.target.value)} dir="auto"
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm">
            <option value="">{t('finance.obligations.payoutStatus')}</option>
            <option value="pending">{t('finance.obligations.payoutStatuses.pending')}</option>
            <option value="received">{t('finance.obligations.payoutStatuses.received')}</option>
          </select>
          <input type="date" value={payoutDate} onChange={(e) => setPayoutDate(e.target.value)} dir="ltr"
            placeholder={t('finance.obligations.payoutDate')}
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono" />
        </div>
      )}

      {kind === 'swimming' && (
        <input type="text" value={goalLink} onChange={(e) => setGoalLink(e.target.value)} maxLength={120}
          placeholder={t('finance.obligations.goalLink')}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" />
      )}

      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
        placeholder={t('finance.obligations.notes')}
        className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none" />

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting || !name.trim() || !amount} className="flex-1">
          {t('common.save')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </form>
  )
}

function ObligationCard({ obligation, onEdit, onDelete, onMarkComplete }) {
  const { t } = useTranslation()
  const meta = KIND_META[obligation.kind] || KIND_META.custom
  const Icon = meta.icon

  const progress = (() => {
    const total = Number(obligation.total_months) || null
    const remaining = Number(obligation.remaining_months)
    if (!total || !Number.isFinite(remaining)) return null
    const paid = Math.max(0, total - remaining)
    return { paid, total, pct: Math.min(100, Math.round((paid / total) * 100)) }
  })()

  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          <Icon className={cn('w-5 h-5 mt-0.5 flex-shrink-0', meta.tone)} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold" dir="auto">{obligation.name}</p>
              <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded" dir="auto">
                {t(`finance.obligations.kinds.${obligation.kind}`)}
              </span>
              {obligation.payout_status === 'received' && (
                <span className="text-[10px] text-emerald-600 bg-emerald-500/10 ring-1 ring-emerald-500/30 px-1.5 py-0.5 rounded flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  {t('finance.obligations.payoutStatuses.received')}
                </span>
              )}
              {obligation.payout_status === 'pending' && (
                <span className="text-[10px] text-amber-600 bg-amber-500/10 ring-1 ring-amber-500/30 px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {t('finance.obligations.payoutStatuses.pending')}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 font-mono" dir="ltr">
              {fmt(obligation.amount)} SAR · {t(`finance.obligations.frequencies.${obligation.frequency}`)}
            </p>

            {progress && (
              <div className="mt-2">
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-app-primary-500 transition-all"
                    style={{ width: `${progress.pct}%` }}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 font-mono" dir="ltr">
                  {progress.paid} / {progress.total} {t('finance.obligations.monthsPaid')}
                </p>
              </div>
            )}

            {obligation.frequency === 'every_6_months' && obligation.next_payment_date && (
              <p className="text-[11px] text-muted-foreground mt-1" dir="auto">
                {t('finance.obligations.nextPayment')}:{' '}
                <span className="font-mono" dir="ltr">{obligation.next_payment_date}</span>
              </p>
            )}

            {obligation.goal_link && (
              <p className="text-[11px] text-muted-foreground mt-1 italic" dir="auto">
                🎯 {obligation.goal_link}
              </p>
            )}

            {obligation.notes && (
              <p className="text-[11px] text-muted-foreground mt-1 whitespace-pre-wrap" dir="auto">
                {obligation.notes}
              </p>
            )}
          </div>
          <RowActionsMenu
            ariaLabel={t('common.actions')}
            actions={[
              { icon: Edit2,  label: t('common.edit'),   onClick: onEdit },
              { icon: Trash2, label: t('common.delete'), onClick: onDelete, variant: 'destructive' },
            ]}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export function SpecialObligationsSection({ data }) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const confirm = useConfirm()

  const handleSave = async (values) => {
    setSubmitting(true)
    if (editing) await data.update(editing.id, values)
    else await data.create(values)
    setSubmitting(false)
    setAdding(false)
    setEditing(null)
  }

  const handleDelete = async (item) => {
    const ok = await confirm({
      title: t('finance.obligations.confirmDelete'),
      description: item.name,
      confirmLabel: t('common.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await data.remove(item.id)
  }

  return (
    <section className="space-y-2 pt-2">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5" dir="auto">
          <Sparkles className="w-4 h-4 text-app-primary-500" />
          {t('finance.obligations.title')}
        </h2>
        {!adding && !editing && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="w-4 h-4 me-1" />
            {t('finance.obligations.add')}
          </Button>
        )}
      </div>

      {adding && (
        <ObligationForm onSave={handleSave} onCancel={() => setAdding(false)} submitting={submitting} />
      )}

      {data.loading ? (
        <Skeleton className="h-20 w-full" />
      ) : data.obligations.length === 0 && !adding ? (
        <EmptyState icon={Sparkles} title={t('finance.obligations.empty')} />
      ) : (
        data.obligations.map((o) => (
          <React.Fragment key={o.id}>
            {editing?.id === o.id ? (
              <ObligationForm initial={o} onSave={handleSave} onCancel={() => setEditing(null)} submitting={submitting} />
            ) : (
              <ObligationCard
                obligation={o}
                onEdit={() => setEditing(o)}
                onDelete={() => handleDelete(o)}
              />
            )}
          </React.Fragment>
        ))
      )}
    </section>
  )
}
